import * as z from 'zod';
import { env } from '@noor/config';
import { http } from '@noor/api';
import {
  orderListEnvelopeSchema,
  orderDetailSchema,
  orderVendorOptionSchema,
  orderCourierOptionSchema,
  orderActorSchema,
  courierLocationSchema,
  courierBatteryLocationSchema,
  orderOffersSchema,
  legacyOffersSchema,
  orderAssignmentSchema,
  orderLogSchema,
  type OrderRow,
  type OrderDetail,
  type OrderCountInfo,
  type CourierLocation,
  type CourierBatteryLocation,
  type OrderOffers,
  type LegacyOffersPayload,
  type OrderAssignment,
  type OrderLog,
  type OrderVendorOption,
  type OrderCourierOption,
  type OrderActor,
} from '../model/order-schema';
import type { OrderListFilter } from '../model/list-params';

const orderStageTabCountsSchema = z.object({
  to_origin: z.coerce.number().default(0),
  wait_order: z.coerce.number().default(0),
  to_destination: z.coerce.number().default(0),
  wait_receiver: z.coerce.number().default(0),
});
export type OrderStageTabCounts = z.infer<typeof orderStageTabCountsSchema>;

/**
 * Outcome of a cancel request. The backend may partially fail — re-ordering a
 * fresh courier and voiding the courier's transactions are best-effort side
 * effects, surfaced to the operator as separate warnings (cpanel parity).
 */
const cancelResultSchema = z
  .object({
    reorder_id: z.union([z.string(), z.number()]).nullish(),
    reorder_error: z.string().nullish(),
    cancel_transactions_error: z.string().nullish(),
  })
  .loose();
export type CancelOrderResult = z.infer<typeof cancelResultSchema>;

/**
 * Real Noor API endpoints for the Order domain (base `…/api/v1`). Each call
 * parses the response with a Zod schema so backend contract drift fails loudly
 * at the boundary (CLAUDE.md). The list envelope is adapted to a table-friendly
 * `{data, meta, countInfo}` shape — the `{count, count_info, current_page,
 * last_page, page_size, data}` mismatch is contained to exactly this file.
 *
 * Endpoint paths follow the admin orders API contract.
 */

/** Adapted list result the table widget consumes. */
export interface OrdersListResult {
  data: OrderRow[];
  meta: {
    page: number;
    pageSize: number;
    /** Total pages (server's `last_page`). */
    totalPages: number;
    /** Total rows (server's `count`). */
    total: number;
  };
  /** Tab badge counts. */
  countInfo: OrderCountInfo;
}

/**
 * Serialize the snake_case filter into a query string. Drops `undefined`/empty
 * values (mirrors `qs.stringify` behavior cpanel relied on). Arrays join CSV.
 */
function toQuery(filter: object): Record<string, string> {
  const query: Record<string, string> = {};
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      query[key] = value.map(serializeScalar).join(',');
    } else {
      query[key] = serializeScalar(value);
    }
  }
  return query;
}

/** Stringify a scalar filter value; non-primitives are skipped (empty string). */
function serializeScalar(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

/** Offers live on the Delivery (search) service, not the main gateway. */
const deliveryHost = { baseURL: env.VITE_DELIVERY_API_URL };

export const ordersApi = {
  async list(filter: OrderListFilter): Promise<OrdersListResult> {
    const raw = await http.get<unknown>('admin/orders', { query: toQuery(filter) });
    const env = orderListEnvelopeSchema.parse(raw);
    const pageSize = env.page_size ?? (Number(filter.limit) || 20);
    return {
      data: env.data ?? [],
      meta: {
        page: env.current_page ?? 1,
        pageSize,
        totalPages: env.last_page ?? 0,
        total: env.count ?? 0,
      },
      countInfo: env.count_info ?? {},
    };
  },

  /** Counts for the four CURRENT delivery status bands used by dispatch. */
  async stageTabCounts(filter: object = {}): Promise<OrderStageTabCounts> {
    const raw = await http.get<unknown>('orders/status-tab-counts', {
      query: toQuery(filter),
    });
    const payload = raw && typeof raw === 'object' && 'data' in raw ? raw.data : raw;
    return orderStageTabCountsSchema.parse(payload ?? {});
  },

  /**
   * Vendor options for the entity filter — cpanel's `InfiniteSelect` over
   * `getVendorsList`: `limit`/`skip` paging, `search`, and the
   * `orders_from_date`/`orders_till_date` window of the current date range.
   */
  async vendorOptions(
    filter: OrderOptionsFilter,
  ): Promise<OrderOptionsPage<OrderVendorOption>> {
    const raw = await http.get<unknown>('admin/vendors', { query: toQuery(filter) });
    return parseOptionsPage(raw, (item) => orderVendorOptionSchema.safeParse(item));
  },

  /** Courier options for the courier filter (cpanel `getCouriers`, same paging). */
  async courierOptions(
    filter: OrderOptionsFilter,
  ): Promise<OrderOptionsPage<OrderCourierOption>> {
    const raw = await http.get<unknown>('admin/couriers', { query: toQuery(filter) });
    return parseOptionsPage(raw, (item) => orderCourierOptionSchema.safeParse(item));
  },

  /**
   * The profile of whoever last updated an order, by role (cpanel
   * `useLastUpdatedBy`): vendors, admin users, or couriers.
   */
  async actor(role: OrderActorRole, id: string | number): Promise<OrderActor> {
    const path =
      role === 'VENDOR'
        ? `admin/vendors/${id}`
        : role === 'USER'
          ? `admin/users/${id}`
          : `admin/couriers/${id}`;
    const raw = await http.get<unknown>(path);
    return orderActorSchema.parse(unwrapData(raw));
  },

  async getById(id: string | number): Promise<OrderDetail> {
    const raw = await http.get<unknown>(`admin/orders/${id}`);
    // The real endpoint answers `{order, transaction, tariff, equipment, …}`:
    // the order is nested and its transactions/tariff/equipment are SIBLINGS
    // (cpanel read `data.transaction`). Fold the siblings back onto the order —
    // dropping them left the transactions table empty. A bare order object
    // (and the mock, which nests everything) passes through untouched.
    const candidate = (() => {
      if (!raw || typeof raw !== 'object' || !('order' in raw)) return raw;
      const { order, ...siblings } = raw;
      return order && typeof order === 'object' ? { ...siblings, ...order } : order;
    })();
    return orderDetailSchema.parse(candidate);
  },

  async assignment(id: string | number): Promise<OrderAssignment> {
    const raw = await http.get<unknown>(`admin/orders/${id}/offers`);
    return orderAssignmentSchema.parse(unwrapData(raw));
  },

  /**
   * Per-courier offers for the offers visualization. Served by the Delivery
   * (search) service, not the main gateway: `{ list: [...], data: {}, meta }`.
   */
  async offers(id: string | number): Promise<OrderOffers> {
    const raw = await http.get<unknown>(
      `/api/v1/admin/orders/${id}/offers`,
      deliveryHost,
    );
    const list = typeof raw === 'object' && raw !== null && 'list' in raw ? raw.list : [];
    return orderOffersSchema.parse(list);
  },

  /**
   * Legacy offers for the visualization — cpanel `getOrderOffers`: the main
   * gateway's route (the one the Assignment tab also reads), whose body is
   * `{ offers, statistics }` with no envelope. A `data`-wrapped body is also
   * tolerated, in case a gateway version envelopes it.
   */
  async legacyOffers(id: string | number): Promise<LegacyOffersPayload> {
    const raw = await http.get<unknown>(`admin/orders/${id}/offers`);
    return legacyOffersSchema.parse(unwrapData(raw));
  },

  /**
   * Order change-log entries (status history). cpanel reads these from a
   * DEDICATED endpoint — the single-order payload does NOT inline `logs[]` (the
   * real backend returns an empty/absent array there), so the detail page must
   * fetch the log feed separately. The response is a bare array (`res.data`),
   * but some deployments wrap it under `data` — tolerate both.
   */
  async logs(id: string | number): Promise<OrderLog[]> {
    const raw = await http.get<unknown>(`admin/orders/${id}/logs`);
    // Resilient fallback, but LOUD: contract drift must not be silent.
    return z
      .array(orderLogSchema)
      .catch((ctx) => {
        console.warn(
          '[orders-api] order logs payload failed schema parse — falling back to []',
          ctx.error,
        );
        return [];
      })
      .parse(unwrapData(raw));
  },

  async courierLocation(
    orderId: string | number,
    courierId: string | number,
  ): Promise<CourierLocation> {
    const raw = await http.get<unknown>(
      `orders/${orderId}/couriers/${courierId}/location`,
    );
    return courierLocationSchema.parse(unwrapData(raw));
  },

  async courierBatteryLocation(
    orderId: string | number,
    courierId: string | number,
  ): Promise<CourierBatteryLocation> {
    const raw = await http.get<unknown>(
      `orders/${orderId}/couriers/${courierId}/battery-location`,
    );
    return courierBatteryLocationSchema.parse(unwrapData(raw));
  },

  /* ── Mutations (write actions; paths preserved from cpanel) ── */

  async cancel(
    id: string | number,
    body: { reason?: string; reorder?: boolean; cancel_transactions?: boolean },
  ): Promise<CancelOrderResult> {
    const raw = await http.patch(`admin/orders/${id}/cancel`, {
      reason: body.reason,
      reorder: body.reorder ?? false,
      cancel_transactions: body.cancel_transactions ?? false,
    });
    // Partial-failure fields are best-effort; tolerate a bare/empty body —
    // but log when the fallback engages so contract drift stays visible.
    return cancelResultSchema
      .catch((ctx) => {
        console.warn(
          '[orders-api] cancel result payload failed schema parse — falling back to {}',
          ctx.error,
        );
        return {};
      })
      .parse(unwrapData(raw));
  },

  async reorder(id: string | number): Promise<unknown> {
    return await http.post(`admin/orders/${id}/re-order`);
  },

  /** Re-offer the order to a SPECIFIC courier (distinct from blind re-order). */
  async reOffer(
    id: string | number,
    courierId: string | number,
    removeOldCourier?: boolean,
  ): Promise<unknown> {
    return await http.post(`admin/orders/${id}/re-offer`, {
      courier_id: courierId,
      ...(removeOldCourier !== undefined ? { remove_old_courier: removeOldCourier } : {}),
    });
  },

  async finish(id: string | number): Promise<unknown> {
    return await http.patch(`admin/orders/${id}/finish`);
  },

  /** Void every cancellable transaction of the order (cpanel `cancel-transactions`). */
  async cancelTransactions(id: string | number): Promise<unknown> {
    return await http.patch(`admin/orders/${id}/cancel-transactions`);
  },

  async return(id: string | number): Promise<unknown> {
    return await http.patch(`admin/orders/${id}/return`);
  },

  async debt(id: string | number, body: { need_debt: boolean }): Promise<unknown> {
    return await http.patch(`admin/orders/${id}/debt`, body);
  },

  async payment(id: string | number, body: unknown): Promise<unknown> {
    return await http.patch(`admin/orders/${id}/payment`, body);
  },

  async courierPaymentSelect(id: string | number): Promise<unknown> {
    return await http.patch(`admin/orders/${id}/courier-payment-select`);
  },

  /** Update an order's destinations/products/delivery (the edit wizard). */
  async changeOrder(id: string | number, payload: unknown): Promise<unknown> {
    return await http.post(`orders/${id}/change-order`, payload);
  },

  /**
   * Download an orders report as a file (Blob). Two variants (cpanel
   * `TableFilters`): `orders` sends the current filter minus pagination;
   * `multi` additionally remaps `from_date`/`till_date` to `date_from`/`date_to`.
   */
  async exportReport(kind: OrderExportKind, filter: OrderListFilter): Promise<Blob> {
    const { limit, skip, ...rest } = filter;
    void limit;
    void skip;
    if (kind === 'multi') {
      const { from_date, till_date, ...base } = rest;
      const query = toQuery({
        ...base,
        date_from: from_date,
        date_to: till_date,
      });
      return await http.getBlob('admin/orders/multi-order-export', { query });
    }
    return await http.getBlob('admin/orders/export', { query: toQuery(rest) });
  },
};

/** Orders export variants (cpanel: `orders` / `multi_orders`). */
export type OrderExportKind = 'orders' | 'multi';

/** Roles whose profile the "last updated by" tooltip can resolve. */
export type OrderActorRole = 'VENDOR' | 'USER' | 'COURIER';

/** Paging + search + orders window for the vendor/courier filter options. */
export interface OrderOptionsFilter {
  limit: number;
  skip: number;
  search?: string | undefined;
  orders_from_date?: string | undefined;
  orders_till_date?: string | undefined;
}

/** One page of filter options plus the server total (`count`). */
export interface OrderOptionsPage<T> {
  data: T[];
  count: number;
}

/** `{data: [...], count}` → typed rows (unparseable rows dropped). */
function parseOptionsPage<T>(
  raw: unknown,
  parse: (item: unknown) => { success: true; data: T } | { success: false },
): OrderOptionsPage<T> {
  const list: unknown[] = isRecord(raw) && Array.isArray(raw.data) ? raw.data : [];
  const data: T[] = [];
  for (const item of list) {
    const result = parse(item);
    if (result.success) data.push(result.data);
  }
  const count = isRecord(raw) && typeof raw.count === 'number' ? raw.count : data.length;
  return { data, count };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Some endpoints wrap the payload under `data`; tolerate both. */
function unwrapData(raw: unknown): unknown {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    return raw.data;
  }
  return raw;
}
